// ===========================================
// Event Settings page (events Release B, #680 wording, #681 booking fee)
// ===========================================
// Wording: what the school calls the thing a family buys: "Buy tickets" for a concert, "Book places"
// for a holiday camp. The school edits both wordings here; each EVENT picks one (or "from the event
// type") in the event editor. The words are RESOLVED on the server; this page only edits them.
//
// Booking fee (#681): a flat fee per paid ticket or once per booking, on or off for the whole school. It is its own charge, not
// part of the card processing fee (Payment settings). Dollars in the box, whole cents on the wire. The
// amount a family is charged is computed on the server; this page only edits the setting.

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import { useEventSettings, useUpdateEventSettings } from '../../hooks/useEvents';
import { usePermissions } from '../../hooks/usePermissions';
import type { BookingFeeMode, CheckoutWords, CheckoutWordingKey, EventSettingsPatch } from '../../api/events.api';
import {
  DEFAULT_CHECKOUT_WORDING,
  CHECKOUT_WORD_MAX_LENGTH,
  checkoutWordError,
  titleCaseWords,
  capitaliseFirst,
  normaliseCheckoutWord,
} from '../../utils/checkoutWords';
import { bookingFeeAmountError, centsToDollarsText, dollarsToCents } from '../../utils/bookingFee';

type Draft = Record<CheckoutWordingKey, CheckoutWords>;
interface FeeDraft {
  enabled: boolean;
  mode: BookingFeeMode;
  amount: string;
}
// Mirrors the server (constants/eventSettings.constants.ts): the server is the authority and re-checks on save.
const DEFAULT_HOLD_LIMIT = 10;
const MAX_HOLD_LIMIT = 100;
/** The limit as typed: a whole number from 1 to 100, or the reason it is not. */
function holdLimitError(text: string): string | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return 'Enter a whole number';
  const n = Number(t);
  if (n < 1) return 'It must be at least 1';
  if (n > MAX_HOLD_LIMIT) return `It can be at most ${MAX_HOLD_LIMIT}`;
  return null;
}
const WORD_FIELDS: Array<{ field: keyof CheckoutWords; label: string; help: string }> = [
  { field: 'verb', label: 'Action word', help: 'The button: "Buy" or "Book"' },
  { field: 'singular', label: 'One', help: 'For one: "ticket" or "place"' },
  { field: 'plural', label: 'Many', help: 'For several: "tickets" or "places"' },
];
const WORDINGS: Array<{ key: CheckoutWordingKey; title: string; blurb: string }> = [
  {
    key: 'ticket',
    title: 'Ticket wording',
    blurb: 'Used by every event except holiday camps, unless an event chooses otherwise.',
  },
  {
    key: 'place',
    title: 'Place wording',
    blurb: 'Used by holiday camps, unless an event chooses otherwise.',
  },
];

export default function EventSettingsPage() {
  const { can } = usePermissions();
  const canRead = can('events', 'read');
  const canWrite = can('events', 'write');
  const { data: settings, isLoading, isError } = useEventSettings(canRead);
  const update = useUpdateEventSettings();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [fee, setFee] = useState<FeeDraft | null>(null);
  const [holdLimit, setHoldLimit] = useState<string | null>(null);
  useEffect(() => {
    if (settings) {
      setHoldLimit(String(settings.seatHolds?.maxPerAddress ?? DEFAULT_HOLD_LIMIT));
      setDraft({ ticket: { ...settings.wording.ticket }, place: { ...settings.wording.place } });
      setFee({
        enabled: settings.bookingFee?.enabled ?? false,
        mode: settings.bookingFee?.mode ?? 'PER_TICKET',
        amount: centsToDollarsText(settings.bookingFee?.amountCents ?? 0),
      });
    }
  }, [settings]);

  const errors = useMemo(() => {
    const out: Record<string, string | null> = {};
    if (!draft) return out;
    for (const { key } of WORDINGS) {
      for (const { field } of WORD_FIELDS) out[`${key}.${field}`] = checkoutWordError(draft[key][field]);
    }
    return out;
  }, [draft]);
  const wordingError = Object.values(errors).some(Boolean);
  // With the switch OFF a blank amount just means 0 (nothing is charged), so it must not block saving the words.
  const feeBlankWhileOff = !!fee && !fee.enabled && fee.amount.trim() === '';
  const feeAmountError = fee && !feeBlankWhileOff ? bookingFeeAmountError(fee.amount) : null;
  const feeCents = fee && !feeAmountError ? (dollarsToCents(fee.amount) ?? 0) : null;
  const wordingDirty =
    !!draft &&
    !!settings &&
    WORDINGS.some(({ key }) => WORD_FIELDS.some(({ field }) => draft[key][field].trim() !== settings.wording[key][field]));
  const feeDirty =
    !!fee &&
    !!settings &&
    (fee.enabled !== (settings.bookingFee?.enabled ?? false) ||
      fee.mode !== (settings.bookingFee?.mode ?? 'PER_TICKET') ||
      (feeCents !== null && feeCents !== (settings.bookingFee?.amountCents ?? 0)));
  const holdError = holdLimit !== null ? holdLimitError(holdLimit) : null;
  const holdDirty =
    holdLimit !== null &&
    !!settings &&
    !holdError &&
    Number(holdLimit.trim()) !== (settings.seatHolds?.maxPerAddress ?? DEFAULT_HOLD_LIMIT);
  const hasError = wordingError || !!feeAmountError || !!holdError;
  const dirty = wordingDirty || feeDirty || holdDirty;

  const setWord = (key: CheckoutWordingKey, field: keyof CheckoutWords, value: string) =>
    setDraft((prev) => (prev ? { ...prev, [key]: { ...prev[key], [field]: value } } : prev));

  const resetWording = (key: CheckoutWordingKey) =>
    setDraft((prev) => (prev ? { ...prev, [key]: { ...DEFAULT_CHECKOUT_WORDING[key] } } : prev));

  const handleSave = () => {
    if (!draft || !fee || hasError) return;
    const trimmed = (w: CheckoutWords): CheckoutWords => ({
      verb: w.verb.trim(),
      singular: w.singular.trim(),
      plural: w.plural.trim(),
    });
    // Only what changed is sent: a fee change never rewrites the words, and the other way round.
    const patch: EventSettingsPatch = {};
    if (wordingDirty) patch.wording = { ticket: trimmed(draft.ticket), place: trimmed(draft.place) };
    if (feeDirty) patch.bookingFee = { enabled: fee.enabled, mode: fee.mode, amountCents: feeCents ?? 0 };
    if (holdDirty && holdLimit !== null) patch.seatHolds = { maxPerAddress: Number(holdLimit.trim()) };
    update.mutate(patch);
  };

  return (
    <Box>
      <PageHeader
        title="Event Settings"
        subtitle="What your school calls the thing families buy or book for an event, and the booking fee."
        breadcrumbs={[{ label: 'Admin', path: '/admin' }, { label: 'Event Settings' }]}
      />

      {!canRead ? (
        <Alert severity="info">You do not have permission to view event settings.</Alert>
      ) : isLoading || ((!draft || !fee) && !isError) ? (
        <Stack spacing={2} data-testid="event-settings-loading">
          <Skeleton variant="rounded" height={220} />
          <Skeleton variant="rounded" height={220} />
        </Stack>
      ) : isError || !draft || !fee ? (
        <Alert severity="error">Event settings could not be loaded. Please refresh the page.</Alert>
      ) : (
        <Stack spacing={3}>
          {WORDINGS.map(({ key, title, blurb }) => {
            const preview = `${capitaliseFirst(normaliseCheckoutWord(draft[key].verb))} ${titleCaseWords(normaliseCheckoutWord(draft[key].plural))}`;
            return (
              <Paper key={key} sx={{ p: 3 }} aria-label={title}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Box>
                    <Typography variant="h6">{title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {blurb}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => resetWording(key)} disabled={!canWrite}>
                    Reset to default
                  </Button>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                  {WORD_FIELDS.map(({ field, label, help }) => {
                    const error = errors[`${key}.${field}`];
                    return (
                      <TextField
                        key={field}
                        label={label}
                        size="small"
                        value={draft[key][field]}
                        onChange={(e) => setWord(key, field, e.target.value)}
                        disabled={!canWrite}
                        error={!!error}
                        helperText={error || help}
                        inputProps={{ maxLength: CHECKOUT_WORD_MAX_LENGTH + 10 }}
                        sx={{ flex: 1 }}
                      />
                    );
                  })}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Families will see: <strong>{preview}</strong>
                </Typography>
              </Paper>
            );
          })}

          <Alert severity="info">
            Ticket tier names (Child, Adult and so on) are not changed by these words, and a paid event picks
            its wording in the event editor.
          </Alert>

          <Paper sx={{ p: 3 }} aria-label="Booking fee">
            <Typography variant="h6">Booking fee</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A flat fee on a paid event, shown to families before they pay and refunded with the tickets. Charge it
              for every paid ticket (free ticket types add none), or once for each booking. It is separate from the
              card processing fee. Switching it off removes it from every event. Each event can use this setting, no
              fee, or its own mode and amount.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'flex-start' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={fee.enabled}
                    onChange={(e) => setFee((prev) => (prev ? { ...prev, enabled: e.target.checked } : prev))}
                    disabled={!canWrite}
                  />
                }
                label="Charge a booking fee"
              />
              <RadioGroup
                aria-label="Booking fee charged"
                value={fee.mode}
                onChange={(e) => setFee((prev) => (prev ? { ...prev, mode: e.target.value as BookingFeeMode } : prev))}
              >
                <FormControlLabel value="PER_TICKET" control={<Radio size="small" disabled={!canWrite} />} label="Per ticket" />
                <FormControlLabel value="PER_BOOKING" control={<Radio size="small" disabled={!canWrite} />} label="Per booking" />
              </RadioGroup>
              <TextField
                label="Fee amount"
                size="small"
                value={fee.amount}
                onChange={(e) => setFee((prev) => (prev ? { ...prev, amount: e.target.value } : prev))}
                disabled={!canWrite}
                error={!!feeAmountError}
                helperText={
                  feeAmountError ||
                  (fee.enabled
                    ? feeCents === 0
                      ? 'The switch is on but the fee is $0.00, so nothing will be charged'
                      : fee.mode === 'PER_BOOKING'
                        ? 'Charged once for each booking that has a paid ticket'
                        : 'Charged once for every paid ticket in an order'
                    : 'Not charged while the switch is off')
                }
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ inputMode: 'decimal' }}
                sx={{ maxWidth: 220 }}
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }} aria-label="Seat holds">
            <Typography variant="h6">Public checkout limit</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Most places one internet connection can hold at once on a public event. It stops someone holding seats
              without paying. Raise it if large groups book from one connection (for example a school or a hall).
            </Typography>
            <TextField
              label="Places per connection"
              size="small"
              value={holdLimit ?? ''}
              onChange={(e) => setHoldLimit(e.target.value)}
              disabled={!canWrite}
              error={!!holdError}
              helperText={holdError || `Between 1 and ${MAX_HOLD_LIMIT}. The usual setting is ${DEFAULT_HOLD_LIMIT}.`}
              inputProps={{ inputMode: 'numeric' }}
              sx={{ maxWidth: 240 }}
            />
          </Paper>

          {canWrite && (
            <Box>
              <Button variant="contained" onClick={handleSave} disabled={!dirty || hasError || update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
